import React from "react";
import { useTranslation } from "react-i18next";
import { Check, ChevronDown } from "lucide-react";

import { useAuth } from "../../app/providers/AuthProvider";
import { SUPPORTED_LANGUAGES, findLanguage } from "../../i18n/languages";
import { FlagIcon } from "../ui/FlagIcon";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";

/**
 * Header flag chip. Session-only language override — never touches the
 * backend or the persisted `user.language`. See `AuthProvider.applySessionLanguage`.
 */
export function LanguageSelector({ variant = "light" }) {
  const { t, i18n } = useTranslation();
  const { applySessionLanguage } = useAuth();

  const code = i18n.language?.startsWith("en") ? "en" : "de";
  const current = findLanguage(code);

  const trigger =
    variant === "dark"
      ? "border-white/15 bg-white/5 text-white hover:bg-white/10"
      : "border-border-subtle bg-surface text-text-primary hover:bg-offwhite";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={t("language.switch_aria")}
          data-testid="language-selector-trigger"
          className={`flex items-center gap-2 rounded-full border pl-1 pr-2.5 py-1 transition-colors ${trigger}`}
        >
          <span
            className="flex h-7 w-7 items-center justify-center overflow-hidden rounded-full ring-1 ring-black/5"
            aria-hidden
          >
            <FlagIcon flag={current.flag} className="h-full w-full" />
          </span>
          <span className="text-[11px] font-semibold uppercase tracking-wide">
            {current.code}
          </span>
          <ChevronDown className="h-3.5 w-3.5 opacity-70" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        role="menu"
        data-testid="language-selector-content"
        className="w-52"
      >
        {SUPPORTED_LANGUAGES.map((lang) => {
          const active = lang.code === code;
          return (
            <DropdownMenuItem
              key={lang.code}
              onSelect={() => {
                if (!active) applySessionLanguage(lang.code);
              }}
              data-testid={`language-option-${lang.code}`}
              className="flex items-center gap-2"
            >
              <span
                className="flex h-5 w-5 items-center justify-center overflow-hidden rounded-full ring-1 ring-black/5"
                aria-hidden
              >
                <FlagIcon flag={lang.flag} className="h-full w-full" />
              </span>
              <span className="flex-1 text-sm">{lang.label}</span>
              {active && (
                <Check
                  className="h-4 w-4 text-champagne"
                  data-testid={`language-option-check-${lang.code}`}
                />
              )}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
